/**
 * Admin V3 — Compliance module — top-level page (cross-driver hub)
 *
 * Phase 4 G5 (Plan §2.2 M-F-13). Architecture V1 §DCA-5 (3-layer audit).
 * G11 — Operational UX completion: this page is now an operational
 * control panel, not just an informational widget.
 *
 * G11.5 — Owner forensic verdict UI remediation:
 *   "search functionality not working" — the search input's placeholder
 *   reads "Rechercher un chauffeur ou un état…", but until G11.5 the
 *   `search` query only narrowed the profiles_by_state aggregate (the
 *   one section where the user is least likely to search). The Pending
 *   Verifications Queue, Drivers Awaiting Review Panel, and Expiring
 *   sections all ignored it. G11.5 wires `search` through to those
 *   sections as a substring match against driver_name / driver_email /
 *   driver_id, so the search input now matches the placeholder's promise.
 *   Pure client-side over the already-fetched dashboard payload.
 *
 * Consumes:
 *   GET /admin/compliance/dashboard
 *     - profiles_by_compliance_state (aggregate)
 *     - expiring_within_30d           (G4.3)
 *     - pending_verifications         (G11 backend extension)
 *
 * Renders:
 *   - Pending Verifications Queue   (G11.A + G11.C + G11.D + G11.5 search)
 *   - Profiles-by-state aggregate   (G4.3, with G11.E deep-link)
 *   - Drivers Awaiting Review       (G11.2 + G11.5 search)
 *   - Expiring-within-30d list      (G4.3, with G11.E deep-link + G11.5 search)
 *
 * Admin V3 remains the SINGLE AUTHORITATIVE administrative surface.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, FileWarning } from 'lucide-react';
import { SectionCard, EmptyState, lifecycleStateMeta, cn } from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import { t, DEFAULT_LOCALE } from '../../i18n';
import CompliancePilotSearchBar from './CompliancePilotSearchBar';
import CompliancePilotTable from './CompliancePilotTable';
import PendingVerificationsQueue from './PendingVerificationsQueue';
import CompliancePilotHeroMetric from './CompliancePilotHeroMetric';
import DriversAwaitingReviewPanel from './DriversAwaitingReviewPanel';

const CompliancePilotPage = () => {
  const locale = DEFAULT_LOCALE;
  const navigate = useNavigate();
  const [stateFilter, setStateFilter] = useState('all');
  const [search, setSearch] = useState('');

  const { data, loading, error, refetch } = useAdminQuery('/admin/compliance/dashboard', {});

  const profilesByState = (data && data.profiles_by_compliance_state) || {};
  const expiring = (data && data.expiring_within_30d) || [];
  const pending = (data && data.pending_verifications) || [];

  // G11.E — Deep-link callbacks. Clicking a profiles-by-state row narrows
  // the DriversList to that compliance state. Clicking an expiring-soon
  // row opens the per-driver Documents tab.
  const handleOpenStateInDriversList = useCallback((state) => {
    navigate(`/admin/drivers?compliance_state=${encodeURIComponent(state)}`);
  }, [navigate]);

  const handleOpenDriver = useCallback((driverId) => {
    navigate(`/admin/drivers/${driverId}`);
  }, [navigate]);

  // Synthesize per-state rows for the table from the aggregate dict.
  const rows = useMemo(() => {
    return Object.entries(profilesByState).map(([state, count]) => ({
      state,
      count,
      meta: lifecycleStateMeta[state] || null,
    })).filter((r) => stateFilter === 'all' || r.state === stateFilter)
       .filter((r) => !search || r.state.toLowerCase().includes(search.toLowerCase()));
  }, [profilesByState, stateFilter, search]);

  // G11.5 — Apply page-level search to the pending queue + drivers awaiting
  // panel + expiring section so the placeholder ("Rechercher un chauffeur
  // ou un état…") matches actual behaviour. Pure client-side substring
  // match across driver_name / driver_email / driver_id.
  const filterByDriverSearch = useCallback((arr) => {
    if (!search || !search.trim()) return arr;
    const q = search.trim().toLowerCase();
    return arr.filter((d) => {
      const haystack = [
        d.driver_name, d.driver_email, d.driver_id,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [search]);

  const filteredPending = useMemo(
    () => filterByDriverSearch(pending),
    [pending, filterByDriverSearch],
  );

  const filteredExpiring = useMemo(
    () => filterByDriverSearch(expiring),
    [expiring, filterByDriverSearch],
  );

  return (
    <div className="p-6 space-y-6" data-testid="compliance-pilot-page">
      <div className="flex items-center justify-between">
        <h1 className="text-[28px] font-semibold text-slate-900">
          {t('compliance.dashboard.title', locale)}
        </h1>
      </div>

      {/* G11.2 — Hero metric: prominent count of documents awaiting review.
          Renders ABOVE filters so the admin's primary action is immediate. */}
      {!loading && !error && (
        <CompliancePilotHeroMetric
          count={pending.length}
          locale={locale}
          onReviewClick={() => {
            // Scroll to the queue section below.
            const el = document.querySelector('[data-testid="compliance-queue-table"]')
                       || document.querySelector('[data-testid="compliance-pilot-page"]');
            el && el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        />
      )}

      <CompliancePilotSearchBar
        search={search}
        onSearchChange={setSearch}
        stateFilter={stateFilter}
        onStateFilterChange={setStateFilter}
        locale={locale}
      />

      {/* G11.5 — When the search input is non-empty, surface a tiny summary
          showing how many drivers/docs matched. Lets the admin verify the
          search input is actually filtering even if no rows render. */}
      {search && search.trim() && !loading && !error && (
        <div
          className="text-sm text-slate-600 -mt-2"
          data-testid="compliance-search-summary"
        >
          {filteredPending.length} / {pending.length}{' '}
          {t('compliance.queue.count', locale)} ·{' '}
          {filteredExpiring.length} / {expiring.length}{' '}
          {t('compliance.dashboard.expiring_30d', locale).toLowerCase()}
        </div>
      )}

      {/* G11.A — Pending Verifications Queue (cross-driver review workspace).
          G11.5 — receives `search` prop so its rendered list reflects the
          page-level search input. */}
      {!loading && !error && (
        <PendingVerificationsQueue
          pending={pending}
          search={search}
          locale={locale}
          onChange={refetch}
        />
      )}

      {/* G11.2 — Drivers Awaiting Review (per-driver grouping).
          G11.5 — uses the search-filtered pending list so the panel respects
          the page-level search. */}
      {!loading && !error && filteredPending.length > 0 && (
        <DriversAwaitingReviewPanel
          pending={filteredPending}
          locale={locale}
        />
      )}

      <SectionCard
        icon={ShieldCheck}
        iconClass="bg-emerald-100 text-emerald-700"
        title={t('compliance.dashboard.profiles_by_state', locale)}
      >
        {loading && <p className="text-slate-500 text-sm">{t('compliance.common.loading', locale)}</p>}
        {error && <p className="text-rose-700 text-sm">{t('compliance.common.error', locale)}</p>}
        {!loading && !error && rows.length === 0 && (
          <EmptyState
            icon={ShieldCheck}
            title={t('compliance.dashboard.empty.title', locale)}
            desc={t('compliance.dashboard.empty.desc', locale)}
          />
        )}
        {!loading && !error && rows.length > 0 && (
          <CompliancePilotTable
            rows={rows}
            locale={locale}
            onRowClick={handleOpenStateInDriversList}
          />
        )}
      </SectionCard>

      <SectionCard
        icon={FileWarning}
        iconClass="bg-amber-100 text-amber-700"
        title={t('compliance.dashboard.expiring_30d', locale)}
      >
        {filteredExpiring.length === 0 ? (
          <p className="text-slate-500 text-sm">—</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-slate-600">
                  <th className="py-2 pr-4">{t('compliance.dashboard.table.driver', locale)}</th>
                  <th className="py-2 pr-4">{t('compliance.documents.table.type', locale)}</th>
                  <th className="py-2 pr-4">{t('compliance.documents.table.expiry', locale)}</th>
                  <th className="py-2 pr-4">{t('compliance.documents.table.actions', locale)}</th>
                </tr>
              </thead>
              <tbody>
                {filteredExpiring.map((d, idx) => (
                  <tr key={d.id || idx} className="border-b border-slate-100">
                    <td className="py-2 pr-4 font-mono text-xs text-slate-700">{d.driver_id}</td>
                    <td className="py-2 pr-4 text-slate-700">{t(`compliance.doctype.${d.document_type}`, locale)}</td>
                    <td className="py-2 pr-4 text-slate-700">{d.expiry_date || '—'}</td>
                    {/* G11.E — Deep-link to the driver's per-driver Documents tab. */}
                    <td className="py-2 pr-4">
                      <button
                        type="button"
                        onClick={() => handleOpenDriver(d.driver_id)}
                        className="text-blue-700 hover:underline text-xs"
                        data-testid={`expiring-open-driver-${d.id || idx}`}
                      >
                        {t('compliance.dashboard.deeplink.openDriver', locale)}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
};

export default CompliancePilotPage;
